import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Link,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import {
  FitnessCenter as FitnessIcon,
  Psychology as PsychologyIcon,
  Speed as SpeedIcon,
  EmojiEvents as TrophyIcon,
  HealthAndSafety as HealthIcon,
  School as SchoolIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Facebook as FacebookIcon,
  Event as EventIcon,
  Groups as GroupsIcon,
  Close as CloseIcon,
  LocationOn as LocationOnIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { getActiveCoachesList, filterCoachesWithUpcomingSlots, getTrainingLocations } from '../utils/firebaseUtils';

const HomePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [registrationSnackbarOpen, setRegistrationSnackbarOpen] = useState(false);
  const [isPageVisible, setIsPageVisible] = useState(true);
  const previousVisibleCoachIdsRef = useRef([]);

  useEffect(() => {
    const handleVisibility = () => setIsPageVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  useEffect(() => {
    if (location.state?.registrationSuccess) {
      setRegistrationSnackbarOpen(true);
      const timer = setTimeout(() => {
        setRegistrationSnackbarOpen(false);
        navigate('/', { replace: true, state: {} });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [location.state?.registrationSuccess, navigate]);

  // Benefits of tennis lessons
  const benefits = [
    {
      icon: <FitnessIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Fizinis aktyvumas',
      description: 'Tenisas puikiai pagerina fizinę formą, stiprina širdį ir plaučius.'
    },
    {
      icon: <PsychologyIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Psichologinis poveikis',
      description: 'Mažina stresą, pagerina nuotaiką, stiprina koncentraciją ir loginį mąstymą.'
    },
    {
      icon: <SpeedIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Greitis ir koordinacija',
      description: 'Tobulina reakcijos greitį, rankų-akių koordinaciją ir erdvinį suvokimą.'
    },
    {
      icon: <TrophyIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Azartas',
      description: 'Suteikia galimybę dalyvauti turnyruose, varžytis su kitais ir siekti savo tikslų.'
    },
    {
      icon: <HealthIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Sveikata',
      description: 'Stiprina kaulus, raumenis ir sąnarius, padeda išvengti sėdimo darbo problemų.'
    }
  ];

  // Training goals
  const goals = [
    {
      icon: <SchoolIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Techniniai Pagrindai',
      description: 'Mokyti taisyklingų techninių pagrindų, kurie užtikrins efektyvų ir saugų žaidimą.'
    },
    {
      icon: <FitnessIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Fizinis Krūvis',
      description: 'Parinkti tinkamą fizinį krūvį treniruotės metu, atsižvelgiant į jūsų fizinę būklę.'
    },
    {
      icon: <PsychologyIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Individualus Požiūris',
      description: 'Parinkti pratimus atitinkančius žaidėjo meistriškumo lygį ir progresą.'
    },
    {
      icon: <TrophyIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Malonus Laikas',
      description: 'Gerai praleisti laiką teniso aikštelėje mokantis ir tobulėjant žaidimo technikoje.'
    }
  ];

  const [coaches, setCoaches] = useState([]);
  const [coachesLoading, setCoachesLoading] = useState(true);
  const [expandedCoachId, setExpandedCoachId] = useState(null);
  const [trainingLocations, setTrainingLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const loadLocations = async () => {
      try {
        const list = await getTrainingLocations();
        if (!cancelled) setTrainingLocations(list);
      } catch (err) {
        console.error(err);
      } finally {
        if (!cancelled) setLocationsLoading(false);
      }
    };
    loadLocations();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const list = await getActiveCoachesList();
        if (cancelled) return;
        const withSlots = await filterCoachesWithUpcomingSlots(list, {
          previousVisibleIds: previousVisibleCoachIdsRef.current
        });
        if (cancelled) return;
        const ordered = withSlots || [];
        previousVisibleCoachIdsRef.current = ordered.map((c) => c.id);
        setCoaches(ordered.map((c) => ({
          ...c,
          photo: c.photoURL ?? null,
          phone: c.phone ?? '',
          email: c.email ?? ''
        })));
      } finally {
        if (!cancelled) setCoachesLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);
  const selectedCoach = coaches.find((c) => c.id === expandedCoachId) || null;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* About TenisoUžsiėmimai.lt Section */}
      <Box sx={{ py: 4 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h4"
              component="h2"
              sx={{
                fontWeight: 600,
                mb: 3,
                fontSize: { xs: '1.5rem', md: '2rem' },
                color: 'text.primary'
              }}
            >
              Kas yra TenisoUžsiėmimai.lt?
            </Typography>
            <Typography variant="body1" sx={{ maxWidth: 800, mx: 'auto', lineHeight: 1.6, color: 'text.secondary', mb: 3 }}>
              TenisoUžsiėmimai.lt tai vieta sujungianti tenisininką su treneriu. Patogiai, greitai ir efektyviai galite pasirinkti jums tinkamą laiką treniruotei.
            </Typography>
            
            <Typography variant="body1" sx={{ maxWidth: 800, mx: 'auto', lineHeight: 1.6, color: 'text.secondary', mb: 3 }}>
              Trenerio darbo kasdienybės pagalbininkas, skaitmenizavimo įrankis.
            </Typography>

            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
              Rezervacijos sistemoje galite:
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 3,
                width: '100%',
                maxWidth: 900,
                mx: 'auto'
              }}
            >
              <Box
                component={RouterLink}
                to="/rezervacija"
                sx={{ textDecoration: 'none', flex: { xs: '1 1 100%', md: 1 }, minWidth: 0 }}
              >
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                      borderColor: 'primary.main'
                    }
                  }}
                >
                  <CardContent sx={{ p: 3.5, display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 3 }}>
                    <Box
                      sx={{
                        width: 72,
                        height: 72,
                        borderRadius: 2,
                        bgcolor: 'primary.main',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}
                    >
                      <EventIcon sx={{ fontSize: 36 }} />
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 1, fontSize: '1.1rem' }}>
                        Rezervuoti treniruotę
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.5, fontSize: '0.95rem' }}>
                        Pasirinkite laiką ir vietą individualiai treniruotei
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
              <Box
                component={RouterLink}
                to="/rezervacija/grupines"
                sx={{ textDecoration: 'none', flex: { xs: '1 1 100%', md: 1 }, minWidth: 0 }}
              >
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                      borderColor: 'primary.main'
                    }
                  }}
                >
                  <CardContent sx={{ p: 3.5, display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 3 }}>
                    <Box
                      sx={{
                        width: 72,
                        height: 72,
                        borderRadius: 2,
                        bgcolor: 'primary.main',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}
                    >
                      <GroupsIcon sx={{ fontSize: 36 }} />
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 1, fontSize: '1.1rem' }}>
                        Pateikti pageidavimą dėl grupinių treniruočių
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.5, fontSize: '0.95rem' }}>
                        Pasakykite, kada Jums tiktų grupinės treniruotės
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Container maxWidth="lg" sx={{ pt: 6, pb: 2 }}>
        <Typography
          variant="h2"
          component="h1"
          sx={{
            textAlign: 'center',
            fontWeight: 700,
            mb: 4,
            color: 'text.primary',
            fontSize: { xs: '2rem', md: '3rem' }
          }}
        >
          Teniso treniruočių nauda
        </Typography>
        
        <Box sx={{ maxWidth: 800, mx: 'auto' }}>
          {benefits.map((benefit, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 3,
                mb: 4,
                p: 3,
                bgcolor: 'background.paper',
                borderRadius: 2,
                boxShadow: 1
              }}
            >
              <Box sx={{ mt: 0.5 }}>
                {benefit.icon}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mb: 1 }}>
                  {benefit.title}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {benefit.description}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Container>

      {/* Goals Section */}
      <Box sx={{ bgcolor: 'grey.50', pt: 4, pb: 6 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            component="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 700,
              mb: 4,
              color: 'text.primary',
              fontSize: { xs: '2rem', md: '3rem' }
            }}
          >
            Treniruočių tikslai
          </Typography>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center' }}>
            {goals.map((goal, index) => (
              <Card
                key={index}
                sx={{
                  width: 450,
                  p: 3,
                  textAlign: 'center',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: 4
                  }
                }}
              >
                <Box sx={{ mb: 2 }}>
                  {goal.icon}
                </Box>
                <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mb: 2 }}>
                  {goal.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {goal.description}
                </Typography>
              </Card>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Treniruotes vedame - training locations, scrollable cards with map preview */}
      <Container maxWidth="lg" sx={{ pt: 6, pb: 2 }}>
        <Typography
          variant="h2"
          component="h2"
          sx={{
            textAlign: 'center',
            fontWeight: 700,
            mb: 4,
            color: 'text.primary',
            fontSize: { xs: '2rem', md: '3rem' }
          }}
        >
          Treniruotes vedame:
        </Typography>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            justifyContent: { xs: 'flex-start', md: 'center' },
            overflowX: 'auto',
            overflowY: 'hidden',
            pb: 2,
            px: 1,
            minHeight: 220,
            '&::-webkit-scrollbar': { height: 8 },
            '&::-webkit-scrollbar-thumb': { bgcolor: 'grey.400', borderRadius: 1 },
            '&::-webkit-scrollbar-track': { bgcolor: 'grey.100' }
          }}
        >
          {locationsLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', minHeight: 180 }}>
              <CircularProgress />
            </Box>
          ) : (
            trainingLocations.map((loc) => (
              <Card
                key={loc.id}
                component="a"
                href={loc.googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  flexShrink: 0,
                  width: 280,
                  minHeight: 200,
                  cursor: 'pointer',
                  overflow: 'hidden',
                  textDecoration: 'none',
                  color: 'inherit',
                  boxShadow: 2,
                  transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                  '&:hover': {
                    boxShadow: 4,
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                <Box
                  sx={{
                    height: 120,
                    bgcolor: 'grey.200',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {loc.mapEmbedUrl && isPageVisible ? (
                    <Box
                      component="iframe"
                      src={loc.mapEmbedUrl}
                      title={loc.name}
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        border: 0,
                        pointerEvents: 'none'
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'grey.300'
                      }}
                    >
                      <LocationOnIcon sx={{ fontSize: 48, color: 'grey.500' }} />
                    </Box>
                  )}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 8,
                      right: 8,
                      bgcolor: 'rgba(255,255,255,0.95)',
                      borderRadius: 1,
                      px: 1,
                      py: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    }}
                  >
                    <OpenInNewIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.main' }}>
                      Žemėlapyje
                    </Typography>
                  </Box>
                </Box>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {loc.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                    {loc.address || '—'}
                  </Typography>
                </CardContent>
              </Card>
            ))
          )}
        </Box>
        {!locationsLoading && trainingLocations.length === 0 && (
          <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
            Vietų sąrašas tuščias.
          </Typography>
        )}
      </Container>

      {/* Coaches Section - horizontal scroll, same-size cards; click opens dialog */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography
          variant="h2"
          component="h2"
          sx={{
            textAlign: 'center',
            fontWeight: 700,
            mb: 4,
            color: 'text.primary',
            fontSize: { xs: '2rem', md: '3rem' }
          }}
        >
          Treneriai
        </Typography>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            justifyContent: { xs: 'flex-start', md: 'center' },
            overflowX: 'auto',
            overflowY: 'hidden',
            pb: 2,
            px: 1,
            minHeight: 320,
            '&::-webkit-scrollbar': { height: 8 },
            '&::-webkit-scrollbar-thumb': { bgcolor: 'grey.400', borderRadius: 1 },
            '&::-webkit-scrollbar-track': { bgcolor: 'grey.100' }
          }}
        >
          {coachesLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', minHeight: 280 }}>
              <CircularProgress />
            </Box>
          ) : (
          coaches.map((coach) => (
            <Card
              key={coach.id}
              onClick={() => setExpandedCoachId(coach.id)}
              sx={{
                flexShrink: 0,
                width: 200,
                minHeight: 280,
                cursor: 'pointer',
                overflow: 'hidden',
                boxShadow: 2,
                transition: 'box-shadow 0.2s ease',
                '&:hover': { boxShadow: 4 }
              }}
            >
              {coach.photo ? (
                <Box
                  component="img"
                  src={coach.photo}
                  alt={coach.name}
                  sx={{
                    width: '100%',
                    height: 180,
                    objectFit: 'cover',
                    bgcolor: 'grey.50'
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    height: 180,
                    bgcolor: 'grey.200',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Nuotrauka bus įkelta vėliau
                  </Typography>
                </Box>
              )}
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
                  {coach.name}
                </Typography>
              </CardContent>
            </Card>
          ))
        )}
        </Box>

        {/* Floating dialog with full coach info */}
        <Dialog
          open={!!selectedCoach}
          onClose={() => setExpandedCoachId(null)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          {selectedCoach && (
            <>
              <DialogTitle sx={{ pb: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h5" component="span" sx={{ fontWeight: 600 }}>
                  {selectedCoach.name}
                </Typography>
                <IconButton aria-label="close" onClick={() => setExpandedCoachId(null)} size="small">
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent dividers sx={{ pt: 2 }}>
                {selectedCoach.photo ? (
                  <Box
                    sx={{
                      width: 160,
                      height: 240,
                      maxWidth: '100%',
                      borderRadius: 2,
                      mb: 2,
                      overflow: 'hidden',
                      mx: 'auto'
                    }}
                  >
                    <Box
                      component="img"
                      src={selectedCoach.photo}
                      alt={selectedCoach.name}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      width: 160,
                      height: 240,
                      maxWidth: '100%',
                      bgcolor: 'grey.200',
                      borderRadius: 2,
                      mb: 2,
                      mx: 'auto',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Nuotrauka bus įkelta vėliau
                    </Typography>
                  </Box>
                )}
                <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7, color: 'text.secondary' }}>
                  {selectedCoach.description}
                </Typography>
                {(selectedCoach.phone || selectedCoach.email || selectedCoach.facebook) && (
                  <>
                    <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600 }}>
                      Kontaktinė informacija
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {selectedCoach.phone && (
                        <Link
                          href={`tel:${selectedCoach.phone}`}
                          sx={{ color: 'primary.main', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 1, '&:hover': { textDecoration: 'underline' } }}
                        >
                          <PhoneIcon /> <Typography component="span">{selectedCoach.phone}</Typography>
                        </Link>
                      )}
                      {selectedCoach.email && (
                        <Link
                          href={`mailto:${selectedCoach.email}`}
                          sx={{ color: 'primary.main', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 1, '&:hover': { textDecoration: 'underline' } }}
                        >
                          <EmailIcon /> <Typography component="span">{selectedCoach.email}</Typography>
                        </Link>
                      )}
                      {selectedCoach.facebook && (
                        <Link
                          href={selectedCoach.facebook}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ color: 'primary.main', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 1, '&:hover': { textDecoration: 'underline' } }}
                        >
                          <FacebookIcon /> <Typography component="span">Facebook</Typography>
                        </Link>
                      )}
                    </Box>
                  </>
                )}
              </DialogContent>
            </>
          )}
        </Dialog>
      </Container>

      {/* Online Booking Section */}
      <Box sx={{ bgcolor: 'primary.main', py: 6 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', color: 'white' }}>
            <Typography
              variant="h2"
              component="h2"
              sx={{
                fontWeight: 700,
                mb: 3,
                fontSize: { xs: '2rem', md: '3rem' }
              }}
            >
              Rezervuokite trenerį internetu
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
              Lengvai ir greitai užsiregistruokite į teniso treniruotes
            </Typography>
            <Button
              component={RouterLink}
              to="/rezervacija"
              variant="contained"
              size="large"
              sx={{
                bgcolor: 'white',
                color: 'primary.main',
                px: 4,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 600,
                '&:hover': {
                  bgcolor: 'grey.100'
                }
              }}
            >
              Rezervuoti dabar
            </Button>
          </Box>
        </Container>
      </Box>

      <Snackbar
        open={registrationSnackbarOpen}
        autoHideDuration={3000}
        onClose={() => setRegistrationSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ mt: 2 }}
      >
        <Alert severity="success" onClose={() => setRegistrationSnackbarOpen(false)}>
          Jūsų registracija priimta – ačiū Jums.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HomePage; 